const factory = (
  benchLocation,
  blConfirmService,
  FullScreenConfirmDialogService,
  ngDialog,
  $window,
) => {
  const localStorageKey = 'lastActiveSessionTime';

  let disabled = false;

  let warningTimerID = null;
  let logoutTimerID = null;

  // default values for timeout periods
  const minutesToMilliSeconds = 60000;
  let timeoutLengthInMilliSeconds = 25 * minutesToMilliSeconds;
  let warningLengthInMilliSeconds = 5 * minutesToMilliSeconds;

  class TimeoutWarningService {
    $disable() {
      disabled = true;
    }

    $$clearTimer(timerID) {
      let id = timerID;
      if (id) {
        $window.clearTimeout(id);
        id = null;
      }
    }

    $$createWarningConfirmDialog() {
      const warningMessage =
        'Your account has been inactive for a<br>while. ' +
        "We'll log you out shortly to<br>keep your account secure.";
      const iconMarkup = '<span class="bl-icon-notification-bell ngdialog-icon_warning"></span>';
      const titleMarkup = '<br><p class="ngdialog-title">Still there?</p>';
      const buttonLabel = 'Stay Logged In';

      const action = () => this.$extendTimer(Date.now(), this.$getLogoutTimeoutLength());

      blConfirmService
        .openConfirm(warningMessage, iconMarkup, titleMarkup, '', buttonLabel, '', false)
        .then(action);
    }

    $extendTimer(lastActiveSessionTime, timeoutInMilliSeconds) {
      this.$setTimer(timeoutInMilliSeconds, lastActiveSessionTime);
    }

    $$getLastActiveSessionTime() {
      return $window.localStorage.getItem(localStorageKey);
    }

    $getLogoutTimeoutLength() {
      return timeoutLengthInMilliSeconds;
    }

    $hasSessionTimedOut(timeoutTimestamp, lastActiveSessionTime, timeoutInMilliSeconds) {
      return timeoutTimestamp + timeoutInMilliSeconds >= lastActiveSessionTime;
    }

    $initializeTimeout(timeoutInMilliSeconds, callback) {
      return $window.setTimeout(callback, timeoutInMilliSeconds);
    }

    $$initializeLogoutTimeout(timeoutTimestamp, timeoutInMilliSeconds) {
      return this.$initializeTimeout(timeoutInMilliSeconds, () => {
        const hasSessionTimedOut = this.$hasSessionTimedOut(
          timeoutTimestamp,
          this.$$getLastActiveSessionTime(),
          timeoutInMilliSeconds,
        );

        if (hasSessionTimedOut) {
          ngDialog.closeAll();
          this.$$showTimeoutMessage();
        } else {
          this.$extendTimer(Date.now(), this.$getLogoutTimeoutLength());
        }
      });
    }

    $$initializeWarningTimeout(warningTimestamp, timeoutInMilliSeconds) {
      return this.$initializeTimeout(timeoutInMilliSeconds, () => {
        const lastActiveSessionTime = this.$$getLastActiveSessionTime();
        const isAnotherTabActive = !this.$hasSessionTimedOut(
          warningTimestamp,
          lastActiveSessionTime,
          warningLengthInMilliSeconds,
        );

        if (isAnotherTabActive) {
          this.$extendTimer(lastActiveSessionTime, this.$getLogoutTimeoutLength());
        } else {
          this.$$showWarningMessage(warningTimestamp);
        }
      });
    }

    $resetTimer() {
      const now = Date.now();

      this.$$setLastActiveSessionTime(now);
      this.$setTimer(this.$getLogoutTimeoutLength(), now);
    }

    $$setLastActiveSessionTime(lastActiveSessionTime) {
      window.localStorage.setItem(localStorageKey, lastActiveSessionTime);
    }

    $setLogoutTimeoutLength(timeoutInMilliSeconds) {
      timeoutLengthInMilliSeconds = timeoutInMilliSeconds;
    }

    $setWarningTimeoutLength(timeoutInMilliSeconds) {
      warningLengthInMilliSeconds = timeoutInMilliSeconds;
    }

    $setTimer(timeoutInMilliSeconds, timestamp) {
      this.$$clearTimer(warningTimerID);
      this.$$clearTimer(logoutTimerID);
      this.$setLogoutTimeoutLength(timeoutInMilliSeconds);

      warningTimerID = this.$$initializeWarningTimeout(timestamp, timeoutInMilliSeconds);
    }

    $$showTimeoutMessage() {
      const timeoutMessage = 'We thought you left, so for your security we logged you out.';
      const icon = 'bench-icon';
      const title = 'Busy with other things?';
      const actionLabel = 'Log Back In';

      this.$$clearTimer(logoutTimerID);
      FullScreenConfirmDialogService.openConfirm(icon, title, timeoutMessage, actionLabel, () =>
        benchLocation.logout(),
      );
    }

    $$showWarningMessage(warningDisplayedTimestamp) {
      if (disabled) return;

      this.$$createWarningConfirmDialog();
      logoutTimerID = this.$$initializeLogoutTimeout(
        warningDisplayedTimestamp,
        warningLengthInMilliSeconds,
      );
    }
  }

  return new TimeoutWarningService();
};

const deps = [
  require('ui/web/confirmDialog').name,
  require('ui/web/fullScreenConfirmDialog').name,
  require('data/util/fullScreenConfirmDialogService').name,
];

module.exports = angular
  .module('bench.service.timeoutWarningService', deps)
  .factory('TimeoutWarningService', factory);
